import type { IAddress } from "@bbj-types/address";
import {
  Autocomplete,
  type AutocompleteProps,
  type AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  TextField,
  type TextFieldProps,
  Typography,
} from "@mui/material";
import type React from "react";
import { forwardRef } from "react";
import { useAddress } from "./logics";

export type TAddressTextFieldProps = Omit<TextFieldProps, "label">;

export interface IAddressProps<DisableClearable extends boolean | undefined>
  extends Omit<
    AutocompleteProps<IAddress | null, undefined, DisableClearable, undefined>,
    "options" | "isOptionEqualToValue" | "getOptionLabel" | "renderInput" | "multiple" | "freeSolo"
  > {
  label?: string;
  TextFieldProps?: TAddressTextFieldProps;
  disableGoogle?: true;
}

export const Address = forwardRef(function Address<DisableClearable extends boolean | undefined = undefined>(
  { label, onInputChange, TextFieldProps, disableGoogle, ...props }: IAddressProps<DisableClearable>,
  ref: React.Ref<unknown> | undefined
) {
  const { options, apiHookLoading, handleInputChange } = useAddress(props.value, onInputChange, disableGoogle);

  const input = (params: AutocompleteRenderInputParams) => (
    <TextField
      label={label}
      type="text"
      {...params}
      {...TextFieldProps}
      sx={{
        "#input-address-helper-text": {
          margin: 0,
        },
      }}
      slotProps={{
        input: { ...params.InputProps, ...TextFieldProps?.InputProps },
        htmlInput: { ...params.inputProps, ...TextFieldProps?.inputProps }
      }} />
  );

  const noOption = (
    <Box>
      {apiHookLoading ? (
        <CircularProgress size={15} />
      ) : (
        <Typography variant="body1" color="textSecondary">
          Pas de résultat
        </Typography>
      )}
    </Box>
  );

  return (
    <Autocomplete
      ref={ref}
      options={options}
      renderInput={input}
      onInputChange={handleInputChange}
      getOptionLabel={(option: IAddress | null) => {
        if (option) {
          let formatted = option.formatted || "";
          if (formatted === "") {
            if (option.city && option.postalCode) {
              formatted = `${option.city} (${option.postalCode})`;
            } else if (option.city) {
              formatted = option.city;
            }
          }
          return formatted;
        }
        return "";
      }}
      isOptionEqualToValue={(option: IAddress | null, value: IAddress | null) =>
        Boolean(option && value && option.formatted === value.formatted)
      }
      filterOptions={(options: (IAddress | null)[]) => options}
      noOptionsText={noOption}
      {...props}
    />
  );
});
